import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    linkedSignal,
    signal,
    untracked,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { invoiceDisplayName, InvoiceFormRequest, PricingStore } from "../../s25.event.pricing.store";
import { Invoice, LineItem, Occurrence } from "../../../../../pojo/Pricing";
import { S25Util } from "../../../../../util/s25-util";
import { OccDataMap, S25PricingUtil } from "../../../s25.pricing.util";
import { WSReservations } from "../../../../s25-event/EventMicroI";
import { Table } from "../../../../s25-table/Table";
import { S25TableModule } from "../../../../s25-table/s25.table.module";
import { S25TriggerRerenderModule } from "../../../../s25-trigger-rerender/s25.trigger.rerender.module";
import { S25ModalModule } from "../../../../s25-modal/s25.modal.module";
import { S25ModalComponent } from "../../../../s25-modal/s25.modal.component";
import { S25CheckboxModule } from "../../../../s25-checkbox/s25.checkbox.module";
import { S25RadioModule } from "../../../../s25-radio/s25.radio.module";
import { S25DatepickerModule } from "../../../../s25-datepicker/s25.datepicker.module";
import { S25ButtonComponent } from "../../../../../standalone/s25.button.component";
import { S25IconComponent } from "../../../../s25-icon/s25.icon.component";
import { S25CollapseChevronComponent } from "../../../../s25-collapse-chevron/s25.collapse.chevron.component";
import { S25FadingSuccessMsgComponent } from "../../../../s25-fading-success-msg/s25.fading.success.msg.component";
import { S25MoveItemAdjustmentsComponent } from "../move-item/s25.move.item.adjustments.component";
import { InvoiceMove } from "../move-item/invoice-move";
import { Bind } from "../../../../../decorators/bind.decorator";
import { InvoiceForm } from "./invoice-form";
import { isDefaultSet } from "../system-sets/system-sets";
type DatedOccurrence = WSReservations & { rsrvId: number };

type FormOccurrence = { rsrvId: number; eventId: number | undefined; label: string } & Pick<
    WSReservations,
    "rsrvStartDt" | "rsrvEndDt"
>;

function startTime(occurrence: DatedOccurrence) {
    return occurrence.rsrvStartDt ? S25Util.date.parse(occurrence.rsrvStartDt).getTime() : 0;
}

/** Labelled the way the rest of pricing does: dates alone, with times when dates would repeat. */
function occurrenceLabels(occurrences: DatedOccurrence[], formats: { date?: string; time?: string } | undefined) {
    const labels = new Map<number, string>();
    if (!formats?.date || !formats?.time) {
        for (const occurrence of occurrences) labels.set(occurrence.rsrvId, occurrence.occurrence ?? "");
        return labels;
    }

    // formatOccDates reads the dates off the map and rewrites `occurrence` on the rows it is given.
    const occData: OccDataMap = { dateFormat: formats.date, timeFormat: formats.time };
    for (const occurrence of occurrences) occData[occurrence.rsrvId] = occurrence as unknown as Occurrence;

    const rows: Partial<Occurrence>[] = occurrences.map((occurrence) => ({
        rsrvId: occurrence.rsrvId,
        occurrence: occurrence.occurrence ?? undefined,
    }));
    S25PricingUtil.formatOccDates(rows, occData);
    rows.forEach((row, index) => labels.set(occurrences[index].rsrvId, row.occurrence ?? ""));

    return labels;
}

/** A requirement can repeat across related events, so a row needs both ids to identify itself. */
function requirementRowId(requirementId: number, eventId: number) {
    return `${requirementId}-${eventId}`;
}

@TypeManagerDecorator("s25-ng-invoice-form")
@Component({
    selector: "s25-ng-invoice-form",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25ModalModule,
        S25TableModule,
        S25TriggerRerenderModule,
        S25CheckboxModule,
        S25RadioModule,
        S25DatepickerModule,
        S25ButtonComponent,
        S25IconComponent,
        S25CollapseChevronComponent,
        S25FadingSuccessMsgComponent,
        S25MoveItemAdjustmentsComponent,
    ],
    template: `
        @if (successMsg()) {
            <s25-ng-fading-success-msg [(message)]="successMsg" />
        }

        <s25-ng-modal #formModal [type]="'custom'" [title]="title()" [size]="'xl'" (modalClosed)="onModalClosed()">
            <ng-template #s25ModalBody>
                <div class="if-body">
                    <div class="if-name-row">
                        <div class="if-field">
                            <label class="if-label" for="invoice-form-name">
                                Name
                                <span class="if-required" aria-hidden="true">*</span>
                            </label>
                            <input
                                id="invoice-form-name"
                                class="c-input"
                                maxlength="80"
                                required
                                [value]="name()"
                                (input)="name.set($any($event.target).value)"
                            />
                        </div>

                        <s25-ng-checkbox [value]="includeEventType()" (valueChange)="includeEventType.set(!!$event)">
                            Include Event Type{{ eventTypeBilledElsewhere() ? " (already billed)" : "" }}
                        </s25-ng-checkbox>
                    </div>

                    @if (requirementRows().length) {
                        <section class="if-section">
                            <h3 class="if-section-head">
                                <button
                                    type="button"
                                    class="if-panel-toggle"
                                    [attr.aria-expanded]="requirementsExpanded()"
                                    aria-controls="invoice-form-requirements"
                                    (click)="requirementsExpanded.set(!requirementsExpanded())"
                                >
                                    <s25-ng-collapse-chevron
                                        aria-hidden="true"
                                        [collapsedOrientation]="'right'"
                                        [expanded]="requirementsExpanded()"
                                    />
                                    <span>Requirements</span>
                                    <span class="if-panel-count">
                                        ({{ billedRequirementCount() }}/{{ requirementRows().length }} billed)
                                    </span>
                                </button>
                            </h3>
                            <div id="invoice-form-requirements">
                                @if (requirementsExpanded()) {
                                    <s25-ng-table
                                        [caption]="'Invoice requirements'"
                                        [dataSource]="requirementTableConfig()"
                                        [hasSelect]="true"
                                        [hasSelectAll]="true"
                                        [selected]="selectedRequirementRowIds()"
                                        (selectedChange)="onRequirementsSelected($event)"
                                    />
                                }
                            </div>
                        </section>
                    }

                    <section class="if-section">
                        <h3 class="if-section-head">Occurrences</h3>

                        @if (editingDefaultSet()) {
                            <p class="ngFinePrint">
                                The Standard Invoice bills occurrences not billed to other invoices, so its occurrences
                                cannot be selected here.
                            </p>
                        } @else {
                            <div class="if-modes" role="radiogroup" aria-label="How to bill occurrences">
                                <s25-ng-radio
                                    [value]="mode()"
                                    [radioValue]="'list'"
                                    [name]="'invoiceFormBillBy'"
                                    (valueChange)="setMode('list')"
                                    >Select From List</s25-ng-radio
                                >
                                <s25-ng-radio
                                    [value]="mode()"
                                    [radioValue]="'date'"
                                    [name]="'invoiceFormBillBy'"
                                    (valueChange)="setMode('date')"
                                    >Bill By Date Range</s25-ng-radio
                                >
                                @if (canIncludeAll()) {
                                    <s25-ng-radio
                                        [value]="mode()"
                                        [radioValue]="'all'"
                                        [name]="'invoiceFormBillBy'"
                                        (valueChange)="setMode('all')"
                                        >Include All</s25-ng-radio
                                    >
                                }
                            </div>
                        }

                        @if (mode() === "date") {
                            <div class="if-dates">
                                <div class="if-field">
                                    <span class="if-label" aria-hidden="true">Start</span>
                                    <s25-datepicker
                                        [(value)]="rangeStart"
                                        [ariaLabel]="'Included occurrences start date'"
                                    />
                                </div>
                                <div class="if-field">
                                    <span class="if-label" aria-hidden="true">End</span>
                                    <s25-datepicker
                                        [(value)]="rangeEnd"
                                        [ariaLabel]="'Included occurrences end date'"
                                    />
                                </div>
                            </div>
                        } @else if (mode() === "all") {
                            <p class="ngFinePrint">
                                Every occurrence is included. Any occurrence added to this event is billed here
                                automatically, and no other invoice can bill occurrences for this event.
                            </p>
                        }

                        @if (mode() === "list" && invoiceBillingEveryOccurrence(); as allOccsInvoice) {
                            <div class="cn-alert cn-alert--warning">
                                <s25-ng-icon [type]="'warningYield'" [ariaLabel]="false" />
                                <span>
                                    <button type="button" class="c-textButton" (click)="goToInvoice(allOccsInvoice.id)">
                                        {{ displayName(allOccsInvoice) }}
                                    </button>
                                    was created to include all occurrences. Occurrences may only be billed to one
                                    invoice.
                                </span>
                            </div>
                        }

                        @if (rangeInverted()) {
                            <p class="cn-alert cn-alert--danger" role="alert">Start date must be before end date.</p>
                        }

                        @if (conflicts().length; as conflictCount) {
                            <div class="cn-alert cn-alert--danger" role="alert">
                                <div class="if-alert-body">
                                    <p>
                                        {{ conflictCount === 1 ? "This occurrence is" : "These occurrences are" }}
                                        already billed to
                                        {{ conflictCount === 1 ? "another invoice" : "other invoices" }}. An occurrence
                                        may only be billed to one invoice.
                                    </p>
                                    <ul class="if-occ-list">
                                        @for (conflict of conflicts(); track conflict.rsrvId) {
                                            <li>
                                                <s25-ng-icon [type]="'calendarCalendarX'" [ariaLabel]="false" />
                                                {{ conflict.label }}
                                                <button
                                                    type="button"
                                                    class="c-textButton"
                                                    (click)="goToInvoice(conflict.invoiceId)"
                                                >
                                                    [{{ conflict.invoiceName }}]
                                                </button>
                                            </li>
                                        }
                                    </ul>
                                </div>
                            </div>
                        }

                        @if (losingBilling().length) {
                            <div class="cn-alert cn-alert--warning">
                                <div class="if-alert-body">
                                    <p>These occurrences will be removed from this invoice.</p>
                                    <ul class="if-occ-list">
                                        @for (occurrence of losingBilling(); track occurrence.rsrvId) {
                                            <li>
                                                <s25-ng-icon [type]="'calendarCalendar'" [ariaLabel]="false" />
                                                {{ occurrence.label }}
                                            </li>
                                        }
                                    </ul>
                                </div>
                            </div>
                        }

                        <button
                            type="button"
                            class="if-panel-toggle"
                            [attr.aria-expanded]="occurrencesExpanded()"
                            aria-controls="invoice-form-occurrences"
                            (click)="occurrencesExpanded.set(!occurrencesExpanded())"
                        >
                            <s25-ng-collapse-chevron
                                aria-hidden="true"
                                [collapsedOrientation]="'right'"
                                [expanded]="occurrencesExpanded()"
                            />
                            <span>{{ occurrencePanelLabel() }}</span>
                            <span class="if-panel-count">({{ occurrencePanelCount() }})</span>
                        </button>

                        <div id="invoice-form-occurrences">
                            @if (occurrencesExpanded()) {
                                @if (mode() === "list") {
                                    <s25-ng-table
                                        [caption]="occurrenceTableCaption()"
                                        [dataSource]="occurrenceTableConfig()"
                                        [hasSelect]="!editingDefaultSet()"
                                        [hasSelectAll]="!editingDefaultSet()"
                                        [selected]="selectedRsrvIds()"
                                        [pivotThreshold]="700"
                                        (selectedChange)="onOccurrencesSelected($event)"
                                    />
                                } @else if (!rangeInverted()) {
                                    <s25-ng-table
                                        *s25-ng-trigger-rerender="rangeTableKey()"
                                        [caption]="'Occurrences this invoice will bill'"
                                        [dataSource]="rangeTableConfig()"
                                        [pivotThreshold]="700"
                                    />
                                }
                            }
                        </div>
                    </section>

                    @if (revealedAdjustments(); as pending) {
                        <section #adjustmentSection id="invoice-form-adjustments" class="if-section">
                            <h3 class="if-section-head">Line Item Adjustments</h3>
                            <s25-ng-move-item-adjustments
                                [item]="pending.item"
                                [itemLabel]="movedItemLabel()"
                                [source]="pending.source"
                                [targetLabel]="name() || 'the new invoice'"
                                [targetIncludesEventType]="includeEventType()"
                                [adjustedItems]="pending.adjustedItems"
                            />
                        </section>
                    }
                </div>
            </ng-template>

            <ng-template #s25ModalFooter>
                <div class="if-footer">
                    @if (allocationPrompt(); as prompt) {
                        <p class="cn-alert cn-alert--warning" role="status">{{ prompt }}</p>
                    }
                    @if (error()) {
                        <p class="cn-alert cn-alert--danger" role="alert">{{ error() }}</p>
                    }
                    <div class="if-footer-buttons">
                        <button type="button" class="aw-button aw-button--outline" (click)="cancel()">Cancel</button>
                        <s25-ng-button
                            [type]="'primary'"
                            [onClick]="save"
                            [disabled]="saveDisabled()"
                            [disabledReason]="saveDisabledReason()"
                        >
                            {{ isEdit() ? "Update Invoice" : "Create Invoice" }}
                        </s25-ng-button>
                    </div>
                </div>
            </ng-template>
        </s25-ng-modal>
    `,
    styles: `
        :host {
            display: block;
        }

        .if-body {
            display: flex;
            flex-direction: column;
            gap: 1.25rem;
        }

        .if-name-row {
            display: flex;
            align-items: flex-end;
            flex-wrap: wrap;
            gap: 1.5rem;
        }

        .if-field {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
        }

        .if-label {
            font-size: 11px;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .if-required {
            color: var(--color--text-danger);
        }

        .if-field .c-input {
            max-width: 30em;
        }

        .if-section {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        .if-section-head {
            margin: 0;
            font-size: 1rem;
            font-weight: 600;
        }

        .if-modes {
            display: flex;
            flex-wrap: wrap;
            gap: 1.25rem;
        }

        .if-dates {
            display: flex;
            flex-wrap: wrap;
            gap: 1.5rem;
        }

        .cn-alert {
            font-size: 0.9rem;
            /* The global .cn-alert centres its children, which reads badly once one of them is a list */
            align-items: flex-start;
        }

        .cn-alert.cn-alert--warning {
            display: flex;
            gap: 0.5rem;
        }

        .cn-alert p {
            margin: 0;
        }

        .if-alert-body {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            min-width: 0;
        }

        /* Flows down five rows, then into the next column, matching the included occurrences list */
        .if-occ-list {
            list-style: none;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
            row-gap: 6px;
            column-gap: 2em;
            max-height: calc(5 * 1.75em + 4 * 6px);
            overflow-x: auto;
        }

        .if-occ-list li {
            width: max-content;
            display: flex;
            align-items: center;
            gap: 0.3rem;
        }

        /* Undoes the global danger alert override that strokes fill-based sprites into blocky outlines */
        :host ::ng-deep .cn-alert s25-ng-icon svg {
            fill: currentColor;
            stroke: none;
        }

        .if-panel-toggle {
            display: flex;
            align-items: center;
            width: fit-content;
            gap: 0.5rem;
            padding: 0;
            background: none;
            border: none;
            color: var(--color--text);
            font: inherit;
            font-weight: 600;
            cursor: pointer;
        }

        .if-panel-toggle:focus-visible {
            outline-offset: 4px;
        }

        .if-panel-count {
            font-weight: 400;
            color: var(--color--text-muted);
        }

        .if-footer {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        .if-footer-buttons {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
        }

        :host ::ng-deep th[data-label="Select"],
        :host ::ng-deep td[data-label="Select"] {
            min-width: 3em;
        }

        :host ::ng-deep s25-ng-table table.s25-ng-table--table {
            table-layout: unset;
        }
    `,
})
export class S25InvoiceFormComponent {
    private readonly pricingStore = inject(PricingStore);

    protected readonly displayName = invoiceDisplayName;

    readonly request = this.pricingStore.invoiceFormRequest;
    readonly isEdit = computed(() => this.request()?.mode === "edit");
    readonly title = computed(() => (this.isEdit() ? "Edit Invoice" : "Create Invoice"));

    private readonly modal = viewChild("formModal", { read: S25ModalComponent });
    private readonly adjustmentForm = viewChild(S25MoveItemAdjustmentsComponent);
    private readonly adjustmentSection = viewChild<ElementRef<HTMLElement>>("adjustmentSection");

    private readonly eventId = this.pricingStore.eventId;
    private readonly combineRelated = this.pricingStore.combineRelated;

    /** Cleared by the request it belongs to, so a closed or reopened form is never left frozen. */
    private readonly saveInFlight = linkedSignal<InvoiceFormRequest | undefined, boolean>({
        source: this.request,
        computation: () => false,
    });

    // A save writes to the store while the form is still on screen, and the invoice being created
    // is no conflict with itself, so the form stops following the list until the save is done.
    // The reference set is left out: it records what the others bill, so every occurrence would
    // read as already billed and every selection as a conflict.
    private readonly invoices = linkedSignal<{ invoices: Invoice[]; frozen: boolean }, Invoice[]>({
        source: () => ({ invoices: this.pricingStore.listedInvoices(), frozen: this.saveInFlight() }),
        computation: ({ invoices, frozen }, previous) => (frozen && previous ? previous.value : invoices),
    });

    readonly error = signal("");
    readonly successMsg = signal("");

    private readonly editedInvoice = computed<Invoice | undefined>(() => {
        const request = this.request();
        if (request?.mode !== "edit") return undefined;
        return this.invoices().find((invoice) => invoice.id === request.invoiceId);
    });

    private readonly movedItem = computed<InvoiceMove.MovableItem | undefined>(() => {
        const request = this.request();
        return request?.mode === "create" ? request.move?.item : undefined;
    });

    private readonly movedFrom = computed<Invoice | undefined>(() => {
        const request = this.request();
        if (request?.mode !== "create" || !request.move) return undefined;
        const sourceInvoiceId = request.move.sourceInvoiceId;
        return this.invoices().find((invoice) => invoice.id === sourceInvoiceId);
    });

    /** An invoice being edited, or one an item is on its way off of, cannot conflict with itself. */
    private readonly exemptInvoiceIds = computed(() =>
        [this.editedInvoice()?.id, this.movedFrom()?.id].filter((id): id is number => S25Util.isDefined(id)),
    );

    constructor() {
        effect(() => {
            const request = this.request();
            const modal = this.modal();
            if (!modal) return;
            if (request) void modal.open();
            else void modal.close();
        });

        // The table only ever appears in answer to a submit, so bring it into view when it does.
        effect(() => this.adjustmentSection()?.nativeElement.scrollIntoView({ behavior: "smooth", block: "start" }));
    }

    // -- Occurrences -----------------------------------------------------------------------------

    private readonly visibleOccurrences = computed<FormOccurrence[]>(() => {
        const eventIdByRsrvId = this.pricingStore.occurrenceEventIds();
        const eventId = this.eventId();
        const combineRelated = this.combineRelated();

        const occurrences = Object.values(this.pricingStore.occurrences())
            .filter((occ): occ is DatedOccurrence => S25Util.isDefined(occ.rsrvId))
            .filter((occ) => combineRelated || eventIdByRsrvId.get(occ.rsrvId) === eventId)
            .sort((first, second) => startTime(first) - startTime(second));

        const labels = occurrenceLabels(occurrences, this.pricingStore.dateFormats());

        return occurrences.map((occ) => ({
            rsrvId: occ.rsrvId,
            eventId: eventIdByRsrvId.get(occ.rsrvId),
            rsrvStartDt: occ.rsrvStartDt,
            rsrvEndDt: occ.rsrvEndDt,
            label: labels.get(occ.rsrvId) ?? "",
        }));
    });

    private readonly occurrenceLabels = computed(
        () => new Map(this.visibleOccurrences().map((occ) => [occ.rsrvId, occ.label])),
    );

    private readonly occurrenceBilling = computed(() =>
        InvoiceForm.occurrenceBillingByRsrvId({
            invoices: this.invoices(),
            exemptInvoiceIds: this.exemptInvoiceIds(),
            eventId: this.eventId(),
            combineRelated: this.combineRelated(),
        }),
    );

    private readonly occurrenceBilledTo = computed(() =>
        InvoiceForm.occurrenceBillingByRsrvId({
            invoices: this.invoices(),
            eventId: this.eventId(),
            combineRelated: this.combineRelated(),
        }),
    );

    readonly invoiceBillingEveryOccurrence = computed(() => {
        const exempt = new Set(this.exemptInvoiceIds());
        return this.invoices().find(
            (invoice) => !exempt.has(invoice.id) && !isDefaultSet(invoice) && InvoiceForm.isIncludeAllInvoice(invoice),
        );
    });

    readonly editingDefaultSet = computed(() => isDefaultSet(this.editedInvoice()));

    // -- Form state ------------------------------------------------------------------------------

    readonly mode = linkedSignal<InvoiceFormRequest | undefined, InvoiceForm.OccurrenceBillingMode>({
        source: this.request,
        computation: () => InvoiceForm.invoiceBillingMode(untracked(this.editedInvoice)),
    });

    readonly name = linkedSignal<InvoiceFormRequest | undefined, string>({
        source: this.request,
        computation: () => invoiceDisplayName(untracked(this.editedInvoice)),
    });

    readonly includeEventType = linkedSignal<InvoiceFormRequest | undefined, boolean>({
        source: this.request,
        computation: (request) => {
            if (request?.mode === "create") return !!request.preselect?.includeEventType;
            return !!untracked(this.editedInvoice)?.billDefn?.includeEventType;
        },
    });

    readonly eventTypeBilledElsewhere = computed(() => {
        const exempt = new Set(this.exemptInvoiceIds());
        return this.invoices().some((invoice) => !exempt.has(invoice.id) && !!invoice.billDefn?.includeEventType);
    });

    readonly selectedRsrvIds = linkedSignal<InvoiceFormRequest | undefined, Set<number>>({
        source: this.request,
        computation: (request) => {
            if (request?.mode === "create") return new Set(request.preselect?.rsrvIds ?? []);

            const visible = untracked(this.occurrenceLabels);
            return new Set(
                [...InvoiceMove.invoiceObjectIds(untracked(this.editedInvoice)).rsrvIds].filter((rsrvId) =>
                    visible.has(rsrvId),
                ),
            );
        },
    });

    // Reseeded on every mode change, so switching to a range never inherits the 1900-2100 span an
    // include all invoice is stored with.
    private readonly seededRange = linkedSignal<InvoiceForm.OccurrenceBillingMode, InvoiceForm.DateRange>({
        source: this.mode,
        computation: (mode) => {
            const invoice = untracked(this.editedInvoice);
            const existing =
                mode === "date" && !InvoiceForm.isIncludeAllInvoice(invoice)
                    ? InvoiceForm.invoiceDateRange(invoice)
                    : undefined;
            return existing ?? { start: new Date(), end: new Date() };
        },
    });

    readonly rangeStart = linkedSignal(() => this.seededRange().start);
    readonly rangeEnd = linkedSignal(() => this.seededRange().end);

    private readonly range = computed<InvoiceForm.DateRange | undefined>(() => {
        const mode = this.mode();
        if (mode === "all") return InvoiceForm.allOccurrencesRange();
        if (mode === "date") return { start: this.rangeStart(), end: this.rangeEnd() };
        return undefined;
    });

    readonly rangeInverted = computed(() => InvoiceForm.isRangeInverted(this.range()));

    /**
     * Include all is on offer while no custom invoice bills an occurrence of this event, and always
     * for an invoice already on it - otherwise the mode it opens in has no radio to sit on. The
     * default and reference sets only hold what custom invoices leave, so they do not count.
     */
    readonly canIncludeAll = computed(
        () =>
            ![...this.occurrenceBilling().values()].some(InvoiceForm.isClaimed) ||
            InvoiceForm.isIncludeAllInvoice(this.editedInvoice()),
    );

    // -- What the invoice will bill --------------------------------------------------------------

    private readonly rangeOccurrences = computed(() =>
        InvoiceForm.occurrencesInRange(this.visibleOccurrences(), this.range()),
    );

    private readonly candidateRsrvIds = computed<number[]>(() => {
        if (this.mode() === "list") {
            const visible = this.occurrenceLabels();
            return [...this.selectedRsrvIds()].filter((rsrvId) => visible.has(rsrvId));
        }
        return this.rangeOccurrences().map((occ) => occ.rsrvId);
    });

    readonly conflicts = computed(() =>
        InvoiceForm.conflictingOccurrences({
            candidateRsrvIds: this.candidateRsrvIds(),
            invoices: this.invoices(),
            exemptInvoiceIds: this.exemptInvoiceIds(),
            eventId: this.eventId(),
            combineRelated: this.combineRelated(),
            labels: this.occurrenceLabels(),
        }),
    );

    readonly losingBilling = computed(() =>
        this.isEdit()
            ? InvoiceForm.occurrencesLosingBilling({
                  invoice: this.editedInvoice(),
                  candidateRsrvIds: this.candidateRsrvIds(),
                  labels: this.occurrenceLabels(),
              })
            : [],
    );

    // -- Requirements ----------------------------------------------------------------------------

    private readonly requirementBilling = computed(() =>
        InvoiceForm.requirementBillingByRequirementId({
            invoices: this.invoices(),
            exemptInvoiceIds: this.exemptInvoiceIds(),
            eventId: this.eventId(),
            combineRelated: this.combineRelated(),
        }),
    );

    readonly requirementRows = computed(() => {
        const eventId = this.eventId();
        const combineRelated = this.combineRelated();
        const billing = this.requirementBilling();
        const locators: Record<number, string> = this.pricingStore.eventLocatorMap();

        return this.pricingStore
            .eventRequirements()
            .filter((requirement) => combineRelated || requirement.eventId === eventId)
            .map((requirement) => ({
                ...requirement,
                rowId: requirementRowId(requirement.requirementId, requirement.eventId),
                eventLocator: locators[requirement.eventId] ?? "",
                billedTo: billing.get(requirement.requirementId),
            }));
    });

    readonly billedRequirementCount = computed(
        () => this.requirementRows().filter((requirement) => !!requirement.billedTo).length,
    );

    readonly selectedRequirementRowIds = linkedSignal<InvoiceFormRequest | undefined, Set<Table.Row["id"]>>({
        source: this.request,
        computation: (request) => {
            const preselected =
                request?.mode === "create"
                    ? new Set(request.preselect?.requirementIds ?? [])
                    : InvoiceMove.invoiceObjectIds(untracked(this.editedInvoice)).reqIds;

            return new Set(
                untracked(this.requirementRows)
                    .filter((requirement) => preselected.has(requirement.requirementId))
                    .map((requirement) => requirement.rowId),
            );
        },
    });

    private readonly selectedRequirements = computed(() => {
        const selected = this.selectedRequirementRowIds();
        return this.requirementRows().filter((requirement) => selected.has(requirement.rowId));
    });

    // -- Panels ----------------------------------------------------------------------------------

    // Collapsed panels never mount their table, so these stay local to the component
    readonly requirementsExpanded = signal(false);
    readonly occurrencesExpanded = signal(true);

    readonly occurrencePanelLabel = computed(() => (this.mode() === "list" ? "Occurrences" : "Included Occurrences"));

    readonly occurrencePanelCount = computed(() =>
        this.mode() === "list" ? this.visibleOccurrences().length : this.rangeOccurrences().length,
    );

    // -- Tables ----------------------------------------------------------------------------------

    /** The columns the old pricing set showed, with the occurrence label standing in for start and end. */
    private readonly occurrenceColumns = computed<Table.Column[]>(() => {
        const combineRelated = this.combineRelated();
        const segmented = this.pricingStore.hasMultipleProfiles();

        return [
            { id: "billedTo", header: "Billed To" },
            ...(combineRelated ? [{ id: "ref", header: "Reference" }] : []),
            { id: "name", header: "Name" },
            { id: "title", header: "Title" },
            ...(segmented
                ? [
                      { id: "segment", header: "Segment" },
                      { id: "segComment", header: "Segment Comment" },
                  ]
                : []),
            { id: "occurrence", header: "Occurrence" },
            { id: "comment", header: "Comment" },
            { id: "locations", header: "Locations" },
            { id: "resources", header: "Resources" },
        ];
    });

    // The table calls dataSource well after the config is built, so everything reactive a row shows
    // is resolved out here, where the table configs below pick up a dependency on it.
    private readonly occurrenceRowMapper = computed(() => {
        const details = this.pricingStore.occurrenceDetails();
        const billing = this.occurrenceBilledTo();
        const exemptInvoiceIds = new Set(this.exemptInvoiceIds());
        const locators: Record<number, string> = this.pricingStore.eventLocatorMap();
        const combineRelated = this.combineRelated();
        const segmented = this.pricingStore.hasMultipleProfiles();

        return (occ: FormOccurrence, selectable?: boolean): Table.Row => {
            const detail = details.get(occ.rsrvId);
            const billedTo = billing.get(occ.rsrvId);

            return {
                id: occ.rsrvId,
                name: occ.label,
                ...(selectable !== undefined && { selectable }),
                cells: {
                    billedTo: { text: InvoiceForm.billedToLabel(billedTo, exemptInvoiceIds) },
                    ...(combineRelated && { ref: { text: locators[occ.eventId ?? -1] ?? "" } }),
                    name: { text: detail?.eventName ?? "" },
                    title: { text: detail?.eventTitle ?? "" },
                    ...(segmented && {
                        segment: { text: detail?.profileName ?? "" },
                        segComment: { text: detail?.profileComment ?? "" },
                    }),
                    occurrence: { text: occ.label },
                    comment: { text: detail?.comments ?? "" },
                    locations: { text: detail?.locations.join(", ") ?? "" },
                    resources: { text: detail?.resources.join(", ") ?? "" },
                },
            };
        };
    });

    readonly occurrenceTableCaption = computed(() =>
        this.editingDefaultSet()
            ? "Occurrences billed to the Standard Invoice"
            : "Occurrences available to this invoice",
    );

    readonly occurrenceTableConfig = computed<Table.DataSource>(() => {
        const occurrences = this.visibleOccurrences();
        const billing = this.occurrenceBilling();
        const alreadySelected = untracked(this.selectedRsrvIds);
        const columns = this.occurrenceColumns();
        const toRow = this.occurrenceRowMapper();

        return {
            type: "unpaginated",
            columns,
            dataSource: () =>
                Promise.resolve({
                    rows: occurrences.map((occ) =>
                        toRow(occ, !InvoiceForm.isClaimed(billing.get(occ.rsrvId)) || alreadySelected.has(occ.rsrvId)),
                    ),
                }),
        };
    });

    /** A new range means a new set of rows, and this table has no selection state worth keeping. */
    readonly rangeTableKey = computed(() =>
        this.rangeOccurrences()
            .map((occ) => occ.rsrvId)
            .join("-"),
    );

    readonly rangeTableConfig = computed<Table.DataSource>(() => {
        const occurrences = this.rangeOccurrences();
        const columns = this.occurrenceColumns();
        const toRow = this.occurrenceRowMapper();

        return {
            type: "unpaginated",
            columns,
            dataSource: () => Promise.resolve({ rows: occurrences.map((occ) => toRow(occ)) }),
        };
    });

    readonly requirementTableConfig = computed<Table.DataSource>(() => {
        const requirements = this.requirementRows();
        const combineRelated = this.combineRelated();

        return {
            type: "unpaginated",
            columns: [
                { id: "billedTo", header: "Billed To" },
                ...(combineRelated ? [{ id: "ref", header: "Reference" }] : []),
                { id: "reqName", header: "Name" },
            ],
            dataSource: () =>
                Promise.resolve({
                    // Unlike an occurrence, a requirement can be billed on more than one invoice, so
                    // billing it elsewhere is worth saying but never takes the row out of play.
                    rows: requirements.map(
                        (requirement): Table.Row => ({
                            id: requirement.rowId,
                            name: requirement.requirementName,
                            cells: {
                                billedTo: {
                                    text: requirement.billedTo ? `Yes (${requirement.billedTo.invoiceName})` : "No",
                                },
                                ...(combineRelated && { ref: { text: requirement.eventLocator } }),
                                reqName: { text: requirement.requirementName },
                            },
                        }),
                    ),
                }),
        };
    });

    // -- Adjustment reallocation -----------------------------------------------------------------

    readonly movedItemLabel = computed(() => {
        const item = this.movedItem();
        if (!item) return "";
        if (item.rsrvId) return this.occurrenceLabels().get(item.rsrvId) ?? "";
        return (
            this.requirementRows().find((requirement) => requirement.requirementId === item.requirementId)
                ?.requirementName ?? ""
        );
    });

    /**
     * Whether the invoice being created ends up billing the item that was moved here. It is only
     * taken off its old invoice if so, since the user is free to leave it out again.
     */
    private readonly willBillMovedItem = computed(() => {
        const item = this.movedItem();
        if (!item) return false;
        if (item.requirementId) {
            return this.selectedRequirements().some((requirement) => requirement.requirementId === item.requirementId);
        }
        return !!item.rsrvId && this.candidateRsrvIds().includes(item.rsrvId);
    });

    /** Adjustments the item carries off its old invoice, which the new invoice can take over. */
    readonly pendingAdjustments = computed(() => {
        const item = this.movedItem(),
            source = this.movedFrom();
        if (!item || !source || !this.willBillMovedItem()) return undefined;

        const adjustedItems: LineItem[] = InvoiceMove.adjustmentsInPlay({
            item,
            source,
            targetIncludesEventType: this.includeEventType(),
        });
        if (!adjustedItems.length) return undefined;

        return { item, source, adjustedItems };
    });

    /** The allocation table is asked for only once the user has tried to create the invoice. */
    readonly adjustmentsRevealed = linkedSignal<InvoiceFormRequest | undefined, boolean>({
        source: this.request,
        computation: () => false,
    });

    readonly revealedAdjustments = computed(() => (this.adjustmentsRevealed() ? this.pendingAdjustments() : undefined));

    readonly allocationPrompt = computed(() =>
        this.revealedAdjustments() ? "Allocate the line item adjustments below, then select Create Invoice again." : "",
    );

    // -- Actions ---------------------------------------------------------------------------------

    readonly saveDisabled = computed(() => !this.name().trim() || this.rangeInverted() || !!this.conflicts().length);

    readonly saveDisabledReason = computed(() => {
        if (!this.name().trim()) return "Please enter a name for this invoice";
        if (this.rangeInverted()) return "Start date must be before end date";
        if (this.conflicts().length) return "Occurrences billed to another invoice must be excluded";
        return "";
    });

    setMode(mode: InvoiceForm.OccurrenceBillingMode) {
        this.error.set("");
        this.mode.set(mode);
    }

    onOccurrencesSelected(selected: Set<Table.Row["id"]>) {
        this.selectedRsrvIds.set(new Set([...selected].map(Number).filter((rsrvId) => Number.isFinite(rsrvId))));
    }

    onRequirementsSelected(selected: Set<Table.Row["id"]>) {
        this.selectedRequirementRowIds.set(new Set(selected));
    }

    goToInvoice(invoiceId: number) {
        this.pricingStore.closeInvoiceForm();
        this.pricingStore.setEvBillId(invoiceId);
    }

    cancel() {
        this.pricingStore.closeInvoiceForm();
    }

    onModalClosed() {
        this.error.set("");
        this.pricingStore.closeInvoiceForm();
    }

    @Bind
    async save() {
        const request = this.request();
        if (!request || this.saveDisabled()) return;

        // Adjustments the new invoice could take over are allocated before it is created, so the
        // first submit only opens that table. Validation above runs again on the submit after it.
        if (this.pendingAdjustments() && !this.adjustmentsRevealed()) {
            this.adjustmentsRevealed.set(true);
            return;
        }

        // Closing the modal destroys the allocation form, so take its payload first.
        const adjustments = this.adjustmentForm()?.adjustmentPayload() ?? [];
        const selection = {
            name: this.name().trim(),
            eventIds: this.payloadEventIds(),
            rsrvIds: this.mode() === "list" ? this.candidateRsrvIds() : [],
            requirementIds: [...new Set(this.selectedRequirements().map((requirement) => requirement.requirementId))],
            includeEventType: this.includeEventType(),
            dateRange: this.range(),
        };

        this.error.set("");
        const invoice = this.editedInvoice();
        const move = request.mode === "create" && this.willBillMovedItem() ? request.move : undefined;

        this.saveInFlight.set(true);
        const saved: { error?: string; invoiceId?: number } =
            request.mode === "edit" && invoice
                ? await this.saveEdit(selection, invoice)
                : await this.pricingStore.createInvoice({
                      createData: InvoiceForm.createPayload(selection),
                      move,
                      adjustments,
                  });

        // A refused save leaves the form up, where it has to answer for what the store holds now.
        if (saved.error) {
            this.saveInFlight.set(false);
            return this.error.set(saved.error);
        }

        this.successMsg.set(request.mode === "edit" ? "Invoice updated" : "Invoice created");
        this.pricingStore.closeInvoiceForm();

        // A create from the summary has nowhere to show what it made, so it opens the new invoice.
        if (request.mode === "create" && request.selectOnCreate && saved.invoiceId) {
            this.pricingStore.setEvBillId(saved.invoiceId);
        }
    }

    /**
     * Dropping a stored date range takes a request of its own, since the server discards a
     * reservation list sent alongside the flag that clears the range.
     */
    private async saveEdit(selection: InvoiceForm.InvoiceFormSelection, invoice: Invoice): Promise<{ error?: string }> {
        const clearRange = InvoiceForm.clearDateRangePayload({
            invoice,
            eventIds: selection.eventIds,
            rsrvIds: selection.rsrvIds,
            dateRange: selection.dateRange,
        });

        if (clearRange) {
            const { error } = await this.pricingStore.updateInvoice(clearRange, invoice.id, { deferMerge: true });
            if (error) return { error };
        }

        const { error } = await this.pricingStore.updateInvoice(
            InvoiceForm.editPayload({ ...selection, invoice }),
            invoice.id,
        );
        // The range is already gone, so what is on screen is no longer what the invoice bills.
        if (error && clearRange) await this.pricingStore.reloadInvoices();
        return { error };
    }

    /** Every event the invoice bills something for, falling back to the events in view. */
    private payloadEventIds() {
        const eventIdByRsrvId = this.pricingStore.occurrenceEventIds();
        const eventIds = new Set<number>();

        for (const rsrvId of this.candidateRsrvIds()) {
            const eventId = eventIdByRsrvId.get(rsrvId);
            if (eventId) eventIds.add(eventId);
        }
        for (const requirement of this.selectedRequirements()) eventIds.add(requirement.eventId);

        return eventIds.size ? [...eventIds] : this.pricingStore.eventIds();
    }
}
